import React from 'react';

import {
  ActionIcon,
  Button,
  Checkbox,
  createStyles,
  MultiSelect,
  Paper,
  Space,
  TextInput,
  Title,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconFilterOff, IconPlus, IconSearch, IconX } from '@tabler/icons-react';
import EventTypeMultiSelectAutoComplete, {
  IEventTypeMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import PackageCategoryAutoComplete, {
  PackageCategoryAutoCompleteValue,
} from 'components/form-fields/auto-complete-fields/PackageCategoryAutoComplete';
import RevenueCategoryMultiSelectAutoComplete, {
  IRevenueCategoryMultiSelectAutoComplete,
} from 'components/form-fields/auto-complete-fields/RevenueCategoryMultiSelectAutoComplete';
import { PackageListView } from 'types';
import { PackageTypeLabelsEnum } from 'types/enums';

type PackageTypeValue = PackageListView['type'];

const packageTypeOptions = (Object.keys(PackageTypeLabelsEnum) as PackageTypeValue[]).map(value => ({
  value,
  label: PackageTypeLabelsEnum[value],
}));

const useStyles = createStyles(theme => ({
  header: {
    padding: theme.spacing.lg,
    background: theme.colors.gray[0],
    borderRadius: 0,
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto auto auto auto min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title search eventTypes packageType packageCategory revenueCategory archived clear button"`,
    alignItems: 'center',
    gap: theme.spacing.sm,
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'auto auto auto auto auto auto min-content min-content',
      gridTemplateRows: 'auto auto',
      gridTemplateAreas: `
        'title title title title title title button button'
        'search eventTypes packageType packageCategory revenueCategory archived clear clear'
      `,
    },
    [theme.fn.smallerThan('sm')]: {
      gridTemplateColumns: 'auto auto max-content min-content',
      gridTemplateRows: 'repeat(4, auto)',
      gridTemplateAreas: `
        'title title button button'
        'search search search search'
        'eventTypes eventTypes packageType packageType'
        'packageCategory revenueCategory archived clear'
      `,
    },
  },
  title: {
    gridArea: 'title',
  },
  search: {
    gridArea: 'search',
    minWidth: '16rem',
  },
  eventTypes: {
    gridArea: 'eventTypes',
  },
  packageCategory: {
    gridArea: 'packageCategory',
  },
  revenueCategory: {
    gridArea: 'revenueCategory',
  },
  packageType: {
    gridArea: 'packageType',
  },
  clear: {
    gridArea: 'clear',
  },
  button: {
    gridArea: 'button',
  },
  archived: {
    gridArea: 'archived',
  },
}));

const PackagesPageHeader = ({
  search,
  setSearch,
  eventTypes,
  setEventTypes,
  packageCategory,
  setPackageCategory,
  revenueCategories,
  setRevenueCategories,
  packageTypes,
  setPackageTypes,
  isArchived,
  setArchived,
  handleOpenCreateForm,
  permission = true,
  headerRef,
}: {
  search: string;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  eventTypes: IEventTypeMultiSelectAutoComplete[];
  packageCategory: PackageCategoryAutoCompleteValue | null;
  setPackageCategory: React.Dispatch<React.SetStateAction<PackageCategoryAutoCompleteValue | null>>;
  revenueCategories: IRevenueCategoryMultiSelectAutoComplete[];
  setRevenueCategories: React.Dispatch<React.SetStateAction<IRevenueCategoryMultiSelectAutoComplete[]>>;
  packageTypes: PackageTypeValue[];
  setPackageTypes: React.Dispatch<React.SetStateAction<PackageTypeValue[]>>;
  isArchived: boolean;
  setArchived: React.Dispatch<React.SetStateAction<boolean>>;
  setEventTypes: React.Dispatch<React.SetStateAction<IEventTypeMultiSelectAutoComplete[]>>;
  handleOpenCreateForm: () => void;
  permission?: boolean;
  headerRef: React.MutableRefObject<any>;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.currentTarget?.value || '';
    setSearch(value);
  };

  const handleEventTypesChange = (values: IEventTypeMultiSelectAutoComplete[]): void => {
    setEventTypes(values);
  };

  const handlePackageCategoryChange = (value: PackageCategoryAutoCompleteValue | null): void => {
    setPackageCategory(value);
  };

  const handleRevenueCategoryChange = (values: IRevenueCategoryMultiSelectAutoComplete[]): void => {
    setRevenueCategories(values);
  };

  const handlePackageTypesChange = (values: string[]): void => {
    setPackageTypes(values as PackageTypeValue[]);
  };

  const handleIsArchivedChange = (): void => {
    setArchived(!isArchived);
  };

  const handleClearSearchFilter = (): void => {
    setSearch('');
  };

  const handleClearFilters = (): void => {
    setSearch('');
    setEventTypes([]);
    setPackageCategory(null);
    setRevenueCategories([]);
    setPackageTypes([]);
    setArchived(false);
  };

  return (
    <Paper className={classes.header} ref={headerRef}>
      <Title order={1} size='h1' pr='xl' className={classes.title}>
        Packages
      </Title>

      <TextInput
        data-cy='searchPackageInput'
        className={classes.search}
        icon={<IconSearch size={theme.fontSizes.md} />}
        placeholder='Search by package name'
        value={search}
        onChange={handleSearchChange}
        rightSection={
          !!search ? (
            <ActionIcon onClick={handleClearSearchFilter} size='md'>
              <IconX size={theme.fontSizes.md} />
            </ActionIcon>
          ) : (
            <Space />
          )
        }
      />

      <EventTypeMultiSelectAutoComplete
        className={classes.eventTypes}
        handleChange={handleEventTypesChange}
        values={eventTypes}
      />
      <MultiSelect
        className={classes.packageType}
        data={packageTypeOptions}
        value={packageTypes}
        onChange={handlePackageTypesChange}
        placeholder='Package Type'
        clearable
        data-cy='packageTypeFilter'
      />
      <PackageCategoryAutoComplete
        className={classes.packageCategory}
        onChange={handlePackageCategoryChange}
        value={packageCategory}
      />

      <RevenueCategoryMultiSelectAutoComplete
        className={classes.revenueCategory}
        handleChange={handleRevenueCategoryChange}
        values={revenueCategories}
      />

      <Checkbox
        className={classes.archived}
        label='Show Archived'
        checked={isArchived}
        onChange={handleIsArchivedChange}
      />

      <Tooltip label='Clear filters' className={classes.clear}>
        <ActionIcon onClick={handleClearFilters} aria-label='Clear filters'>
          <IconFilterOff />
        </ActionIcon>
      </Tooltip>

      <Button
        className={classes.button}
        leftIcon={<IconPlus size={theme.fontSizes.md} />}
        onClick={handleOpenCreateForm}
        disabled={!permission}
        data-cy='openPackageCreateView'
      >
        Package
      </Button>
    </Paper>
  );
};

export default PackagesPageHeader;
